/*
 * Croatian Jewellery — Ring Size Guide
 * Self-contained vanilla JS custom element. No dependency on theme internals.
 *
 * Calibration: CSS pixels, browser-reported screen dimensions, and devicePixelRatio do NOT
 * reliably correspond to real-world physical millimetres across devices — they are never used as
 * a source of physical measurement here. The ONLY source of truth for px-per-mm is the customer
 * physically matching an on-screen shape against a real object of known size:
 *   - Australian $1 coin, 25.00mm diameter (default / most accurate for phones)
 *   - Physical ruler, a 20.00mm line
 *   - Bank/credit card, ISO ID-1, 85.60 x 53.98mm (optional, for larger screens — an 85.60mm
 *     shape does not reliably fit the usable width of many phones in portrait orientation)
 *
 * Custom elements defined:
 *   <ring-size-guide>  — wraps the trigger button + full-screen tool. One per product form.
 *
 * Data attributes read from the host element (set by blocks/ring-size-guide.liquid):
 *   data-product-id            Shopify numeric product id (informational)
 *   data-available-sizes       JSON array of the product's actual "Size" option values, e.g. ["US4","US4.5",...]
 *
 * Everything here is deliberately framework-free so it cannot break if the theme's internal
 * `@theme/*` module map changes. It talks to the theme's native variant picker only through the
 * public DOM contract already used by the theme itself: a radio <input> (or dropdown <select>)
 * inside the <variant-picker> element whose value equals the option value text, dispatching a
 * bubbling native `change` event. That is exactly what assets/variant-picker.js listens for
 * (`this.addEventListener('change', ...)` on the <variant-picker> element itself), so this works
 * without touching or importing that file.
 *
 * RING-SIZE MATH (unchanged from the previous version — do not edit without a specific request):
 * SIZE_TABLE, sizeFromDiameter(), nearestAvailableSize() are untouched.
 */
(function () {
  'use strict';

  if (customElements.get('ring-size-guide')) return;

  /* ----------------------------------------------------------------------
   * 1. RING SIZE DATA (UNCHANGED)
   * ------------------------------------------------------------------- */

  var CARD_WIDTH_MM = 85.60;
  var CARD_HEIGHT_MM = 53.98;
  var COIN_DIAMETER_MM = 25.00; // Australian $1 coin
  var RULER_LENGTH_MM = 20.00;

  // Bumped to v3: calibration METHOD changed (coin/ruler replace card-as-default), so any
  // previously stored calibration is discarded and every customer recalibrates cleanly once.
  var CALIBRATION_KEY = 'cj_ring_size_calibration_v3';

  // Purely a starting visual size for the calibration shapes before the customer adjusts them —
  // NOT used for any real measurement. 3.78 px/mm is the standard CSS reference pixel density
  // (96 CSS px/inch). The customer's physical adjustment is the only thing that ever produces a
  // real calibration value.
  var INITIAL_PX_PER_MM_GUESS = 3.78;

  var SIZE_TABLE = [
    { us: 2.5, mm: 13.7, uk: 'E' },
    { us: 3.0, mm: 14.1, uk: 'F' },
    { us: 3.5, mm: 14.5, uk: 'G' },
    { us: 4.0, mm: 14.9, uk: 'H' },
    { us: 4.5, mm: 15.3, uk: 'I' },
    { us: 5.0, mm: 15.7, uk: 'J 1/2' },
    { us: 5.5, mm: 16.1, uk: 'K 1/2' },
    { us: 6.0, mm: 16.5, uk: 'L 1/2' },
    { us: 6.5, mm: 16.9, uk: 'M 1/2' },
    { us: 7.0, mm: 17.3, uk: 'N 1/2' },
    { us: 7.5, mm: 17.7, uk: 'O 1/2' },
    { us: 8.0, mm: 18.1, uk: 'P 1/2' },
    { us: 8.5, mm: 18.5, uk: 'Q 1/2' },
    { us: 9.0, mm: 18.9, uk: 'R 1/2' },
    { us: 9.5, mm: 19.4, uk: 'S 1/2' },
    { us: 10.0, mm: 19.8, uk: 'T 1/2' },
    { us: 10.5, mm: 20.2, uk: 'U 1/2' },
    { us: 11.0, mm: 20.6, uk: 'V 1/2' },
    { us: 11.5, mm: 21.0, uk: 'W 1/2' },
    { us: 12.0, mm: 21.4, uk: 'Y' },
    { us: 12.5, mm: 21.8, uk: 'Z' },
    { us: 13.0, mm: 22.2, uk: 'Z+1' },
    { us: 13.5, mm: 22.6, uk: 'Z+2' },
    { us: 14.0, mm: 23.0, uk: 'Z+3' },
    { us: 14.5, mm: 23.4, uk: 'Z+4' },
    { us: 15.0, mm: 23.8, uk: 'Z+5' }
  ];

  var MIN_DIAMETER_MM = SIZE_TABLE[0].mm;
  var MAX_DIAMETER_MM = SIZE_TABLE[SIZE_TABLE.length - 1].mm;

  function sizeFromDiameter(diameterMm) {
    var clamped = Math.min(Math.max(diameterMm, MIN_DIAMETER_MM), MAX_DIAMETER_MM);
    var lower = SIZE_TABLE[0];
    var upper = SIZE_TABLE[SIZE_TABLE.length - 1];
    for (var i = 0; i < SIZE_TABLE.length - 1; i++) {
      if (clamped >= SIZE_TABLE[i].mm && clamped <= SIZE_TABLE[i + 1].mm) {
        lower = SIZE_TABLE[i];
        upper = SIZE_TABLE[i + 1];
        break;
      }
    }
    var span = upper.mm - lower.mm;
    var t = span === 0 ? 0 : (clamped - lower.mm) / span;
    var exactUs = lower.us + t * (upper.us - lower.us);
    var nearestRow = Math.abs(clamped - lower.mm) <= Math.abs(upper.mm - clamped) ? lower : upper;
    var circumferenceMm = diameterMm * Math.PI;
    var euSize = Math.round((diameterMm * Math.PI) * 2) / 2;
    return {
      diameterMm: diameterMm,
      circumferenceMm: circumferenceMm,
      exactUsSize: exactUs,
      nearestUsSize: nearestRow.us,
      ukAuSize: nearestRow.uk,
      euSize: euSize,
      outOfRange: diameterMm < MIN_DIAMETER_MM || diameterMm > MAX_DIAMETER_MM
    };
  }

  function nearestAvailableSize(exactUsSize, availableSizes) {
    if (!availableSizes || !availableSizes.length) return null;
    var best = null;
    var bestDiff = Infinity;
    for (var i = 0; i < availableSizes.length; i++) {
      var raw = availableSizes[i];
      var num = parseFloat(String(raw).replace(/[^0-9.]/g, ''));
      if (isNaN(num)) continue;
      var diff = Math.abs(num - exactUsSize);
      if (diff < bestDiff) {
        bestDiff = diff;
        best = raw;
      }
    }
    return best;
  }

  /* ----------------------------------------------------------------------
   * 2. CALIBRATION STORAGE
   * ------------------------------------------------------------------- */

  function normalizedScreenKey() {
    var w = Math.round(window.screen.width);
    var h = Math.round(window.screen.height);
    var lo = Math.min(w, h);
    var hi = Math.max(w, h);
    var dpr = Math.round((window.devicePixelRatio || 1) * 100) / 100;
    return lo + 'x' + hi + '@' + dpr;
  }

  function loadCalibration() {
    try {
      var raw = window.localStorage.getItem(CALIBRATION_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (!data || !data.pxPerMm || !data.screenKey) return null;
      if (data.screenKey !== normalizedScreenKey()) return null; // device/zoom changed — must recalibrate
      return data;
    } catch (e) {
      return null;
    }
  }

  function saveCalibration(pxPerMm, method) {
    var data = {
      pxPerMm: pxPerMm,
      method: method,
      screenKey: normalizedScreenKey(),
      calibratedAt: new Date().toISOString()
    };
    try {
      window.localStorage.setItem(CALIBRATION_KEY, JSON.stringify(data));
    } catch (e) {
      /* localStorage unavailable — calibration just won't persist across visits */
    }
    return data;
  }

  /* ----------------------------------------------------------------------
   * 3. VARIANT SELECTION (talks to the theme's native <variant-picker>)
   * ------------------------------------------------------------------- */

  function applySizeToProductPage(sizeValue) {
    var picker = document.querySelector('variant-picker[data-template-product-match="true"]');
    if (!picker) return { applied: false, reason: 'no-picker' };

    var radios = picker.querySelectorAll('fieldset input[type="radio"]');
    var target = null;
    for (var i = 0; i < radios.length; i++) {
      if (radios[i].value === sizeValue) { target = radios[i]; break; }
    }
    if (target) {
      if (target.checked) return { applied: true, reason: 'already-selected' };
      target.checked = true;
      target.dispatchEvent(new Event('change', { bubbles: true }));
      return { applied: true, reason: 'selected' };
    }

    var options = picker.querySelectorAll('select.variant-option__select option');
    for (var j = 0; j < options.length; j++) {
      if (options[j].value === sizeValue) {
        var select = options[j].closest('select');
        if (select) {
          select.value = sizeValue;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          return { applied: true, reason: 'selected' };
        }
      }
    }

    return { applied: false, reason: 'size-not-found-in-dom' };
  }

  /* ----------------------------------------------------------------------
   * 4. CALIBRATION METHOD METADATA
   * ------------------------------------------------------------------- */

  var CALIB_METHODS = {
    coin: { label: 'Australian $1 coin', desc: 'Recommended — most accurate on phones' },
    ruler: { label: 'Physical ruler', desc: 'Match a 20mm line' },
    card: { label: 'Bank / credit card', desc: 'Optional — primarily for larger screens' }
  };

  function methodSwitcherHtml(current) {
    var html = '<button type="button" class="rsg-link rsg-method-switcher-toggle" data-rsg-toggle-methods>Use another calibration method</button>';
    html += '<div class="rsg-method-switcher" data-rsg-method-switcher hidden>';
    Object.keys(CALIB_METHODS).forEach(function (id) {
      if (id === current) return;
      var m = CALIB_METHODS[id];
      html += '<button type="button" class="rsg-method-switch-btn" data-rsg-switch-method="' + id + '">' +
        '<span class="rsg-method-switch-title">' + m.label + '</span>' +
        '<span class="rsg-method-switch-desc">' + m.desc + '</span>' +
      '</button>';
    });
    html += '</div>';
    return html;
  }

  /* ----------------------------------------------------------------------
   * 5. CUSTOM ELEMENT
   * ------------------------------------------------------------------- */

  function RingSizeGuide() {
    return Reflect.construct(HTMLElement, [], RingSizeGuide);
  }

  RingSizeGuide.prototype = Object.create(HTMLElement.prototype);
  RingSizeGuide.prototype.constructor = RingSizeGuide;

  RingSizeGuide.prototype.connectedCallback = function () {
    this._availableSizes = [];
    try {
      this._availableSizes = JSON.parse(this.getAttribute('data-available-sizes') || '[]');
    } catch (e) {
      this._availableSizes = [];
    }
    this._method = null; // 'ring' | 'finger'
    this._lastResult = null;
    this._calibration = loadCalibration();
    this._hasVariantPicker = !!document.querySelector('variant-picker[data-template-product-match="true"]');

    var btn = this.querySelector('[data-rsg-open]');
    if (btn) btn.addEventListener('click', this._openTool.bind(this));
  };

  RingSizeGuide.prototype._openTool = function () {
    this._step = 'entry';
    this._calibration = loadCalibration();
    this._hasVariantPicker = !!document.querySelector('variant-picker[data-template-product-match="true"]');
    this._renderOverlay();
  };

  RingSizeGuide.prototype._closeTool = function () {
    if (this._overlay) {
      this._overlay.remove();
      this._overlay = null;
    }
    document.documentElement.classList.remove('rsg-scroll-lock');
  };

  RingSizeGuide.prototype._renderOverlay = function () {
    if (this._overlay) this._overlay.remove();

    var overlay = document.createElement('div');
    overlay.className = 'rsg-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Ring Size Guide');
    this._overlay = overlay;
    document.body.appendChild(overlay);
    document.documentElement.classList.add('rsg-scroll-lock');

    this._step = this._step || 'entry';
    this._renderStep();

    overlay.addEventListener('touchmove', function (e) {
      if (e.target.closest('[data-rsg-scrollable]')) return;
      e.preventDefault();
    }, { passive: false });
  };

  RingSizeGuide.prototype._renderStep = function () {
    if (!this._overlay) return;
    var html = '';
    if (this._step === 'entry') html = this._templateEntry();
    else if (this._step === 'calibrate') html = this._templateCalibrate();
    else if (this._step === 'measure-ring') html = this._templateMeasureRing();
    else if (this._step === 'measure-finger') html = this._templateMeasureFinger();
    else if (this._step === 'result') html = this._templateResult();

    this._overlay.innerHTML = html;
    this._wireStep();
  };

  /* ---- ENTRY -------------------------------------------------------- */

  RingSizeGuide.prototype._templateEntry = function () {
    return (
      '<div class="rsg-panel" data-rsg-scrollable>' +
        '<div class="rsg-header">' +
          '<span class="rsg-brand">Croatian Jewellery</span>' +
          '<button type="button" class="rsg-close" data-rsg-close aria-label="Close">&times;</button>' +
        '</div>' +
        '<h2 class="rsg-title">Find Your Ring Size</h2>' +
        '<p class="rsg-sub">Choose how you\'d like to measure. For the most accurate result, measuring an existing ring is recommended.</p>' +
        '<div class="rsg-method-list">' +
          '<button type="button" class="rsg-method-card" data-rsg-method="ring">' +
            '<span class="rsg-method-title">Measure an Existing Ring</span>' +
            '<span class="rsg-method-desc">Place a ring you already own on your screen. Most accurate method.</span>' +
          '</button>' +
          '<button type="button" class="rsg-method-card" data-rsg-method="finger">' +
            '<span class="rsg-method-title">Measure Your Finger</span>' +
            '<span class="rsg-method-desc">Use a strip of paper or string and your screen as a calibrated ruler.</span>' +
          '</button>' +
        '</div>' +
        (this._calibration ? '<button type="button" class="rsg-link" data-rsg-recalibrate>Recalibrate screen</button>' : '') +
      '</div>'
    );
  };

  /* ---- CALIBRATE (coin default / ruler / card) ------------------------ */

  RingSizeGuide.prototype._templateCalibrate = function () {
    if (!this._calibMethod) this._calibMethod = 'coin';
    if (this._calibMethod === 'ruler') return this._templateCalibrateRuler();
    if (this._calibMethod === 'card') return this._templateCalibrateCard();
    return this._templateCalibrateCoin();
  };

  RingSizeGuide.prototype._templateCalibrateCoin = function () {
    if (typeof this._coinRawPx !== 'number') this._coinRawPx = COIN_DIAMETER_MM * INITIAL_PX_PER_MM_GUESS;
    return (
      '<div class="rsg-panel rsg-panel--fullscreen" data-rsg-scrollable>' +
        '<div class="rsg-header">' +
          '<button type="button" class="rsg-back" data-rsg-back aria-label="Back">&larr;</button>' +
          '<span class="rsg-brand">Croatian Jewellery</span>' +
          '<button type="button" class="rsg-close" data-rsg-close aria-label="Close">&times;</button>' +
        '</div>' +
        '<h2 class="rsg-title">Calibrate Your Screen</h2>' +
        '<div class="rsg-coin-stage">' +
          '<p class="rsg-calib-label-top">Place an Australian $1 coin on the screen</p>' +
          '<div class="rsg-coin-circle" data-rsg-coin></div>' +
          '<p class="rsg-calib-label-bottom">Use + and &minus; until the circle matches the outside edge of the coin exactly.</p>' +
        '</div>' +
        '<div class="rsg-slider-row" data-rsg-scrollable>' +
          '<button type="button" class="rsg-nudge" data-rsg-coin-nudge="-0.5">&minus;</button>' +
          '<input type="range" min="30" max="260" step="0.1" value="' + this._coinRawPx + '" class="rsg-range" data-rsg-coin-range aria-label="Coin circle size">' +
          '<button type="button" class="rsg-nudge" data-rsg-coin-nudge="0.5">+</button>' +
        '</div>' +
        '<p class="rsg-hint-small">Reference: Australian $1 coin, 25.00mm diameter.</p>' +
        '<button type="button" class="rsg-primary-btn" data-rsg-confirm-coin>Confirm Calibration</button>' +
        methodSwitcherHtml('coin') +
      '</div>'
    );
  };

  RingSizeGuide.prototype._templateCalibrateRuler = function () {
    if (typeof this._rulerRawPx !== 'number') this._rulerRawPx = RULER_LENGTH_MM * INITIAL_PX_PER_MM_GUESS;
    return (
      '<div class="rsg-panel rsg-panel--fullscreen" data-rsg-scrollable>' +
        '<div class="rsg-header">' +
          '<button type="button" class="rsg-back" data-rsg-back aria-label="Back">&larr;</button>' +
          '<span class="rsg-brand">Croatian Jewellery</span>' +
          '<button type="button" class="rsg-close" data-rsg-close aria-label="Close">&times;</button>' +
        '</div>' +
        '<h2 class="rsg-title">Calibrate Your Screen</h2>' +
        '<div class="rsg-ruler-stage">' +
          '<p class="rsg-calib-label-top">Place a ruler against the line</p>' +
          '<div class="rsg-ruler-line" data-rsg-ruler></div>' +
          '<p class="rsg-calib-label-bottom">Use + and &minus; until the line matches exactly 20mm on your ruler.</p>' +
        '</div>' +
        '<div class="rsg-slider-row" data-rsg-scrollable>' +
          '<button type="button" class="rsg-nudge" data-rsg-ruler-nudge="-0.5">&minus;</button>' +
          '<input type="range" min="20" max="220" step="0.1" value="' + this._rulerRawPx + '" class="rsg-range" data-rsg-ruler-range aria-label="Ruler line length">' +
          '<button type="button" class="rsg-nudge" data-rsg-ruler-nudge="0.5">+</button>' +
        '</div>' +
        '<p class="rsg-hint-small">Reference: 20.00mm on a physical ruler.</p>' +
        '<button type="button" class="rsg-primary-btn" data-rsg-confirm-ruler>Confirm Calibration</button>' +
        methodSwitcherHtml('ruler') +
      '</div>'
    );
  };

  RingSizeGuide.prototype._templateCalibrateCard = function () {
    if (typeof this._cardRawPx !== 'number') this._cardRawPx = CARD_WIDTH_MM * INITIAL_PX_PER_MM_GUESS;
    return (
      '<div class="rsg-panel rsg-panel--fullscreen" data-rsg-scrollable>' +
        '<div class="rsg-header">' +
          '<button type="button" class="rsg-back" data-rsg-back aria-label="Back">&larr;</button>' +
          '<span class="rsg-brand">Croatian Jewellery</span>' +
          '<button type="button" class="rsg-close" data-rsg-close aria-label="Close">&times;</button>' +
        '</div>' +
        '<h2 class="rsg-title">Calibrate Your Screen</h2>' +
        '<p class="rsg-sub">Optional method, primarily for larger screens such as tablets or laptops — an 85.60mm card can be wider than the usable screen on many phones in portrait. Resize the rectangle below until its edges line up exactly with your card.</p>' +
        '<div class="rsg-card-stage">' +
          '<div class="rsg-card-outline" data-rsg-card style="width:' + this._cardRawPx + 'px;">' +
            '<span class="rsg-card-hint">Match this to your card</span>' +
          '</div>' +
        '</div>' +
        '<div class="rsg-slider-row" data-rsg-scrollable>' +
          '<button type="button" class="rsg-nudge" data-rsg-card-nudge="-1">&minus;</button>' +
          '<input type="range" min="150" max="600" step="0.5" value="' + this._cardRawPx + '" class="rsg-range" data-rsg-card-range aria-label="Card width">' +
          '<button type="button" class="rsg-nudge" data-rsg-card-nudge="1">+</button>' +
        '</div>' +
        '<p class="rsg-hint-small">Reference: standard ID-1 bank/credit card (85.60 &times; 53.98mm)</p>' +
        '<button type="button" class="rsg-primary-btn" data-rsg-confirm-card>Confirm Calibration</button>' +
        methodSwitcherHtml('card') +
      '</div>'
    );
  };

  /* ---- MEASURE RING / FINGER ------------------------------------------ */

  RingSizeGuide.prototype._templateMeasureRing = function () {
    return this._templateMeasure('ring');
  };
  RingSizeGuide.prototype._templateMeasureFinger = function () {
    return this._templateMeasure('finger');
  };

  RingSizeGuide.prototype._templateMeasure = function (mode) {
    var isRing = mode === 'ring';
    var min = isRing ? MIN_DIAMETER_MM : (MIN_DIAMETER_MM * Math.PI);
    var max = isRing ? MAX_DIAMETER_MM : (MAX_DIAMETER_MM * Math.PI);
    var start = isRing ? 17.3 : (17.3 * Math.PI);
    return (
      '<div class="rsg-panel rsg-panel--fullscreen" data-rsg-scrollable>' +
        '<div class="rsg-header">' +
          '<button type="button" class="rsg-back" data-rsg-back aria-label="Back">&larr;</button>' +
          '<span class="rsg-brand">Croatian Jewellery</span>' +
          '<button type="button" class="rsg-close" data-rsg-close aria-label="Close">&times;</button>' +
        '</div>' +
        '<h2 class="rsg-title">' + (isRing ? 'Measure the Ring' : 'Measure Your Finger') + '</h2>' +
        '<p class="rsg-sub">' + (isRing
          ? 'Place your ring on the circle below and adjust the size until the black edge sits exactly inside your ring.'
          : 'Wrap a strip of paper or string snugly around the base of your finger and mark the overlap point. Lay it flat, then drag the line below until it matches your marked length.') +
        '</p>' +
        (isRing
          ? '<div class="rsg-ring-stage"><div class="rsg-ring-circle" data-rsg-circle></div></div>'
          : '<div class="rsg-finger-stage"><div class="rsg-finger-line" data-rsg-fingerline></div></div>') +
        '<div class="rsg-readout" data-rsg-readout>' + (isRing ? 'Inner Diameter' : 'Length') + ': --.-- mm</div>' +
        '<div class="rsg-readout-size" data-rsg-readout-size>&nbsp;</div>' +
        '<div class="rsg-slider-row" data-rsg-scrollable>' +
          '<button type="button" class="rsg-nudge" data-rsg-mm-nudge="-0.1">&minus;</button>' +
          '<input type="range" min="' + min + '" max="' + max + '" step="0.02" value="' + start + '" class="rsg-range" data-rsg-mm-range aria-label="' + (isRing ? 'Diameter' : 'Length') + '">' +
          '<button type="button" class="rsg-nudge" data-rsg-mm-nudge="0.1">+</button>' +
        '</div>' +
        '<button type="button" class="rsg-primary-btn" data-rsg-next>Next</button>' +
      '</div>'
    );
  };

  /* ---- RESULT ----------------------------------------------------------- */

  RingSizeGuide.prototype._templateResult = function () {
    var r = this._lastResult;
    if (!r) return '<div class="rsg-panel"><p>Something went wrong. Please try again.</p></div>';

    var available = nearestAvailableSize(r.exactUsSize, this._availableSizes);
    var exactMatch = available && this._availableSizes.some(function (s) {
      return parseFloat(String(s).replace(/[^0-9.]/g, '')) === r.nearestUsSize;
    });

    var usDisplay = 'US ' + trimNum(r.nearestUsSize);
    var euDisplay = 'EU ' + trimNum(r.euSize);

    var useThisSizeBlock;
    if (this._hasVariantPicker && available) {
      useThisSizeBlock =
        '<button type="button" class="rsg-primary-btn" data-rsg-use-size="' + escapeAttr(available) + '">Use This Size</button>' +
        (exactMatch ? '' : '<p class="rsg-hint-small">Closest available size on this product: ' + escapeHtml(available) + '</p>');
    } else if (this._hasVariantPicker) {
      useThisSizeBlock = '<p class="rsg-hint-small">This size isn\'t currently available on this product. Please choose the closest available size manually.</p>';
    } else {
      useThisSizeBlock = '';
    }

    return (
      '<div class="rsg-panel" data-rsg-scrollable>' +
        '<div class="rsg-header">' +
          '<button type="button" class="rsg-back" data-rsg-back aria-label="Back">&larr;</button>' +
          '<span class="rsg-brand">Croatian Jewellery</span>' +
          '<button type="button" class="rsg-close" data-rsg-close aria-label="Close">&times;</button>' +
        '</div>' +
        '<h2 class="rsg-title">Your Ring Size</h2>' +
        '<div class="rsg-result-au">AU Size: ' + escapeHtml(r.ukAuSize) + '</div>' +
        '<div class="rsg-result-secondary">' + usDisplay + ' &middot; ' + euDisplay + '</div>' +
        '<div class="rsg-result-detail">' +
          '<div><span>Inner Diameter</span><strong>' + r.diameterMm.toFixed(2) + ' mm</strong></div>' +
          '<div><span>Inner Circumference</span><strong>' + r.circumferenceMm.toFixed(2) + ' mm</strong></div>' +
        '</div>' +
        (r.outOfRange ? '<p class="rsg-hint-small">This measurement is outside our standard size range — please contact us for a custom size.</p>' : '') +
        useThisSizeBlock +
        '<button type="button" class="rsg-link" data-rsg-restart>Measure again</button>' +
      '</div>'
    );
  };

  /* ---- WIRING ----------------------------------------------------------- */

  RingSizeGuide.prototype._wireStep = function () {
    var self = this;
    var overlay = this._overlay;

    var closeBtn = overlay.querySelector('[data-rsg-close]');
    if (closeBtn) closeBtn.addEventListener('click', function () { self._closeTool(); });

    var backBtn = overlay.querySelector('[data-rsg-back]');
    if (backBtn) backBtn.addEventListener('click', function () { self._goBack(); });

    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) self._closeTool();
    });

    if (this._step === 'entry') {
      var methodBtns = overlay.querySelectorAll('[data-rsg-method]');
      methodBtns.forEach(function (b) {
        b.addEventListener('click', function () {
          self._method = b.getAttribute('data-rsg-method');
          if (self._calibration) {
            self._step = self._method === 'ring' ? 'measure-ring' : 'measure-finger';
          } else {
            self._calibMethod = 'coin'; // coin is always the starting method for a fresh calibration
            self._step = 'calibrate';
          }
          self._renderStep();
        });
      });
      var recal = overlay.querySelector('[data-rsg-recalibrate]');
      if (recal) recal.addEventListener('click', function () {
        self._method = self._method || 'ring';
        self._calibMethod = 'coin';
        self._step = 'calibrate';
        self._renderStep();
      });
    }

    if (this._step === 'calibrate') this._wireCalibrate();
    if (this._step === 'measure-ring' || this._step === 'measure-finger') this._wireMeasure();
    if (this._step === 'result') this._wireResult();
  };

  RingSizeGuide.prototype._goBack = function () {
    var order = { calibrate: 'entry', 'measure-ring': 'entry', 'measure-finger': 'entry', result: this._method === 'ring' ? 'measure-ring' : 'measure-finger' };
    this._step = order[this._step] || 'entry';
    this._renderStep();
  };

  RingSizeGuide.prototype._wireMethodSwitcher = function () {
    var self = this;
    var overlay = this._overlay;
    var toggle = overlay.querySelector('[data-rsg-toggle-methods]');
    var panel = overlay.querySelector('[data-rsg-method-switcher]');
    if (toggle && panel) {
      toggle.addEventListener('click', function () {
        panel.hidden = !panel.hidden;
      });
    }
    overlay.querySelectorAll('[data-rsg-switch-method]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self._calibMethod = btn.getAttribute('data-rsg-switch-method');
        self._renderStep();
      });
    });
  };

  RingSizeGuide.prototype._wireCalibrate = function () {
    if (this._calibMethod === 'ruler') this._wireCalibrateRuler();
    else if (this._calibMethod === 'card') this._wireCalibrateCard();
    else this._wireCalibrateCoin();
    this._wireMethodSwitcher();
  };

  RingSizeGuide.prototype._wireCalibrateCoin = function () {
    var self = this;
    var overlay = this._overlay;
    var circle = overlay.querySelector('[data-rsg-coin]');
    var range = overlay.querySelector('[data-rsg-coin-range]');
    var nudges = overlay.querySelectorAll('[data-rsg-coin-nudge]');

    function applyPx(px) {
      px = Math.min(Math.max(px, 30), 260);
      self._coinRawPx = px;
      circle.style.width = px + 'px';
      circle.style.height = px + 'px';
      range.value = px;
    }
    applyPx(this._coinRawPx);

    range.addEventListener('input', function () { applyPx(parseFloat(range.value)); });
    range.addEventListener('touchstart', function (e) { e.stopPropagation(); }, { passive: true });

    nudges.forEach(function (btn) {
      btn.addEventListener('click', function () {
        applyPx(self._coinRawPx + parseFloat(btn.getAttribute('data-rsg-coin-nudge')));
      });
    });

    overlay.querySelector('[data-rsg-confirm-coin]').addEventListener('click', function () {
      var pxPerMm = self._coinRawPx / COIN_DIAMETER_MM;
      self._calibration = saveCalibration(pxPerMm, 'coin');
      self._step = self._method === 'finger' ? 'measure-finger' : 'measure-ring';
      self._renderStep();
    });
  };

  RingSizeGuide.prototype._wireCalibrateRuler = function () {
    var self = this;
    var overlay = this._overlay;
    var line = overlay.querySelector('[data-rsg-ruler]');
    var range = overlay.querySelector('[data-rsg-ruler-range]');
    var nudges = overlay.querySelectorAll('[data-rsg-ruler-nudge]');

    function applyPx(px) {
      px = Math.min(Math.max(px, 20), 220);
      self._rulerRawPx = px;
      line.style.width = px + 'px';
      range.value = px;
    }
    applyPx(this._rulerRawPx);

    range.addEventListener('input', function () { applyPx(parseFloat(range.value)); });
    range.addEventListener('touchstart', function (e) { e.stopPropagation(); }, { passive: true });

    nudges.forEach(function (btn) {
      btn.addEventListener('click', function () {
        applyPx(self._rulerRawPx + parseFloat(btn.getAttribute('data-rsg-ruler-nudge')));
      });
    });

    overlay.querySelector('[data-rsg-confirm-ruler]').addEventListener('click', function () {
      var pxPerMm = self._rulerRawPx / RULER_LENGTH_MM;
      self._calibration = saveCalibration(pxPerMm, 'ruler');
      self._step = self._method === 'finger' ? 'measure-finger' : 'measure-ring';
      self._renderStep();
    });
  };

  RingSizeGuide.prototype._wireCalibrateCard = function () {
    var self = this;
    var overlay = this._overlay;
    var cardEl = overlay.querySelector('[data-rsg-card]');
    var range = overlay.querySelector('[data-rsg-card-range]');
    var nudges = overlay.querySelectorAll('[data-rsg-card-nudge]');

    function applyWidth(widthPx) {
      widthPx = Math.min(Math.max(widthPx, 150), 600);
      self._cardRawPx = widthPx;
      cardEl.style.width = widthPx + 'px';
      cardEl.style.height = (widthPx * (CARD_HEIGHT_MM / CARD_WIDTH_MM)) + 'px';
      range.value = widthPx;
    }
    applyWidth(this._cardRawPx);

    range.addEventListener('input', function () { applyWidth(parseFloat(range.value)); });
    range.addEventListener('touchstart', function (e) { e.stopPropagation(); }, { passive: true });

    nudges.forEach(function (btn) {
      btn.addEventListener('click', function () {
        applyWidth(self._cardRawPx + parseFloat(btn.getAttribute('data-rsg-card-nudge')));
      });
    });

    overlay.querySelector('[data-rsg-confirm-card]').addEventListener('click', function () {
      var pxPerMm = self._cardRawPx / CARD_WIDTH_MM;
      self._calibration = saveCalibration(pxPerMm, 'card');
      self._step = self._method === 'finger' ? 'measure-finger' : 'measure-ring';
      self._renderStep();
    });
  };

  RingSizeGuide.prototype._wireMeasure = function () {
    var self = this;
    var overlay = this._overlay;
    var pxPerMm = this._calibration ? this._calibration.pxPerMm : 10; // defensive fallback, should never trigger
    var isRing = this._step === 'measure-ring';
    var min = isRing ? MIN_DIAMETER_MM : (MIN_DIAMETER_MM * Math.PI);
    var max = isRing ? MAX_DIAMETER_MM : (MAX_DIAMETER_MM * Math.PI);
    var range = overlay.querySelector('[data-rsg-mm-range]');
    var nudges = overlay.querySelectorAll('[data-rsg-mm-nudge]');
    var readout = overlay.querySelector('[data-rsg-readout]');
    var readoutSize = overlay.querySelector('[data-rsg-readout-size]');
    var visual = isRing ? overlay.querySelector('[data-rsg-circle]') : overlay.querySelector('[data-rsg-fingerline]');

    if (typeof this._measureRaw !== 'number') this._measureRaw = isRing ? 17.3 : (17.3 * Math.PI);

    function render(raw) {
      raw = Math.min(Math.max(raw, min), max);
      self._measureRaw = raw;
      range.value = raw;
      var px = raw * pxPerMm;
      if (isRing) {
        visual.style.width = px + 'px';
        visual.style.height = px + 'px';
      } else {
        visual.style.width = px + 'px';
      }
      readout.textContent = (isRing ? 'Inner Diameter: ' : 'Length: ') + raw.toFixed(2) + ' mm';
      var diameterMm = isRing ? raw : (raw / Math.PI);
      var s = sizeFromDiameter(diameterMm);
      readoutSize.textContent = 'AU ' + s.ukAuSize + ' · US ' + trimNum(s.nearestUsSize);
    }

    render(this._measureRaw);

    range.addEventListener('input', function () { render(parseFloat(range.value)); });
    range.addEventListener('touchstart', function (e) { e.stopPropagation(); }, { passive: true });

    nudges.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var delta = parseFloat(btn.getAttribute('data-rsg-mm-nudge'));
        render(self._measureRaw + delta);
      });
    });

    overlay.querySelector('[data-rsg-next]').addEventListener('click', function () {
      var diameterMm = isRing ? self._measureRaw : (self._measureRaw / Math.PI);
      self._lastResult = sizeFromDiameter(diameterMm);
      self._step = 'result';
      self._renderStep();
    });
  };

  RingSizeGuide.prototype._wireResult = function () {
    var self = this;
    var overlay = this._overlay;
    var useBtn = overlay.querySelector('[data-rsg-use-size]');
    if (useBtn) {
      useBtn.addEventListener('click', function () {
        var sizeValue = useBtn.getAttribute('data-rsg-use-size');
        var res = applySizeToProductPage(sizeValue);
        self._closeTool();
        if (!res.applied) {
          console.warn('Ring Size Guide: could not auto-select size', sizeValue, res.reason);
        }
      });
    }
    var restart = overlay.querySelector('[data-rsg-restart]');
    if (restart) restart.addEventListener('click', function () {
      self._step = self._method === 'finger' ? 'measure-finger' : 'measure-ring';
      self._renderStep();
    });
  };

  /* ---- HELPERS ------------------------------------------------------- */

  function trimNum(n) {
    var r = Math.round(n * 100) / 100;
    return String(r);
  }
  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function escapeAttr(s) { return escapeHtml(s); }

  customElements.define('ring-size-guide', RingSizeGuide);

  // Expose pure functions for isolated unit testing (dev harness only; harmless in production).
  window.__ringSizeGuideInternals = {
    sizeFromDiameter: sizeFromDiameter,
    nearestAvailableSize: nearestAvailableSize,
    SIZE_TABLE: SIZE_TABLE,
    COIN_DIAMETER_MM: COIN_DIAMETER_MM,
    RULER_LENGTH_MM: RULER_LENGTH_MM,
    CARD_WIDTH_MM: CARD_WIDTH_MM
  };
})();
